import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";

import { hasConsent } from "@/lib/consent";

interface Props {
  src: string;
  title: string;
  /** Extra classes applied to the iframe / placeholder fill area. */
  className?: string;
  height?: number | string;
  /** Länk till samma plats på Google Maps. */
  href?: string;
}

/**
 * Google Maps-inbäddning bakom samtycke.
 *
 * Google sätter cookies och hämtar tredjeparts-JS så snart iframen monteras, så
 * kartan laddas bara när besökaren godkänt marknadsföring, eller när hen klickar
 * "Visa karta" för den här sidvisningen (utan att det sparade valet ändras).
 */
export function MapEmbed({
  src,
  title,
  className = "h-full w-full border-0",
  height,
  href,
}: Props) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (hasConsent("marketing")) setLoaded(true);
    const onConsent = () => {
      if (hasConsent("marketing")) setLoaded(true);
    };
    window.addEventListener("tb:consent", onConsent);
    return () => window.removeEventListener("tb:consent", onConsent);
  }, []);

  if (loaded) {
    return (
      <iframe
        src={src}
        title={title}
        className={className}
        height={height}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        style={height ? { border: 0 } : undefined}
      />
    );
  }

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-2xl bg-muted px-6 py-10 text-center"
      style={height ? { height } : undefined}
    >
      <MapPin size={22} className="text-brass-deep" aria-hidden="true" />
      <p className="max-w-xs text-sm text-muted-foreground">
        Kartan laddas från Google Maps och kan sätta cookies.
      </p>
      <button type="button" onClick={() => setLoaded(true)} className="btn-outline text-sm">
        Visa karta
      </button>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm underline hover:text-brass-deep"
        >
          Öppna i Google Maps
        </a>
      ) : null}
      <span className="sr-only">{title}</span>
    </div>
  );
}
